import {Component, OnInit} from '@angular/core';
import {ProjectService} from 'src/app/services/project.service';
import {Project, STATUS_LEVEL_UPDATE_STATE} from 'src/app/models/Project';
import {MediaMatcher} from '@angular/cdk/layout';
import {environment} from 'src/environments/environment';
import {firstValueFrom, Subject, takeUntil, throttleTime} from 'rxjs';
import {SharedService} from 'src/app/services/shared.service';
import {AventiObject} from 'src/app/models/AventiObject';
import {NestedTreeControl} from '@angular/cdk/tree';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {IdService} from 'src/app/services/id.service';
import {Checklist} from 'src/app/models/Checklist';
import {User} from 'src/app/models/User';
import {AventiFile} from 'src/app/models/File';
import {DbpathService} from 'src/app/services/dbpath.service';
import {AdminService} from 'src/app/services/admin.service';
import {ObjectStatus} from 'src/app/models/Status';
import _ from 'underscore';
import {ExcelService} from 'src/app/services/excel.service';
import {ChecklistService} from 'src/app/services/checklist.service';
import {JobService} from 'src/app/services/job.service';
import {JOB_STATUS, JobFrontendState, JobGroup, RtJobGroup, RtJobState} from 'src/app/models/Job';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {PdfRegenerationJobsComponent} from 'src/app/components/projects/pdf-regeneration-jobs/pdf-regeneration-jobs.component';

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.css'],
})
export class ProjectComponent implements OnInit {

  project: Project;
  statuses: ObjectStatus[];
  statusNames: string[];
  objects: AventiObject[];
  checklists: Checklist[];
  receiptforms: Checklist[];
  isLoading = true;
  isLoadingCustomerExport = false;
  projectid: string;
  user: User;
  userUsers: User[];
  adminUsers: User[];
  customerUsers: User[];
  files: AventiFile[];
  filesDbPath: string;
  mobileQuery: MediaQueryList;
  treeControl = new NestedTreeControl<any>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<any>();
  objectsChartData: any;
  checklistsChartData: any;
  chartOptions: any;
  protected readonly STATUS_LEVEL_UPDATE_STATE = STATUS_LEVEL_UPDATE_STATE;
  protected readonly JOB_STATUS = JOB_STATUS;
  pdfImageMaxSizeOptions = [
    {value: 100, label: '100 kB (lav kvalitet)'},
    {value: 250, label: '250 kB'},
    {value: 500, label: '500 kB (standard)'},
    {value: 1000, label: '1 MB'},
    {value: 2000, label: '2 MB'},
    {value: 5000, label: '5 MB (høy kvalitet)'},
    {value: 0, label: 'Ingen bilder i full størrelse'},
  ];
  selectedPdfImageMaxSize = 500;
  pdfRegenerationState: JobFrontendState;
  isRegeneratingProjectPdfs = false;
  latestPdfRegenerationGroup: JobGroup | undefined;
  private pdfRegenerationCompletedToastShown = false;
  private rtPdfRegenerationSnapshot: any[] = [];
  private regenStartedFromGroupId: string | undefined;
  private monotonicPdfRegenerationProgress = 0;
  private monotonicProgressGroupId: string | undefined;
  private ngUnsubscribe = new Subject();

  constructor(
    private projectservice: ProjectService,
    private sharedservice: SharedService,
    media: MediaMatcher,
    private idservice: IdService,
    private dbpathservice: DbpathService,
    private excelService: ExcelService,
    private checklistservice: ChecklistService,
    private adminservice: AdminService,
    private jobservice: JobService,
    private snackbarservice: SnackbarService,
    private dialog: MatDialog,
  ) {
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
    adminservice
      .getObjectStatuses()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((statuses) => {
        this.statuses = _.reject(statuses, _.isNull);
        this.statuses = _.sortBy(this.statuses, 'priority');
        this.statusNames = this.statuses.map(status => status.name);
        this.setChartData();
      });
  }

  hasChild = (num: number, node: any) => !!node.children && node.children.length > 0 && (num || !num);

  ngOnInit(): void {
    this.projectid = this.idservice.projectId();

    this.jobservice.rtDbGetJobStatesForProject(this.projectid, 'pdfRegeneration')
      .pipe(
        // RTDB child progress writes during regen can fire 500+ times in seconds. Collapse the
        // burst so we don't re-run recomputePdfRegenerationState on every tick.
        throttleTime(500, undefined, {leading: true, trailing: true}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(async (rtJob) => {
        this.rtPdfRegenerationSnapshot = rtJob || [];
        await this.recomputePdfRegenerationState();
      });

    this.jobservice.getPdfRegenerationJobGroupsForProject(this.projectid)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (groups) => {
        const newLatest = groups[0];
        if (this.regenStartedFromGroupId && newLatest && newLatest.id !== this.regenStartedFromGroupId) {
          this.regenStartedFromGroupId = undefined;
        }
        this.latestPdfRegenerationGroup = newLatest;
        await this.recomputePdfRegenerationState();
      });

    this.projectservice.getFullProjectById(this.projectid)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (project) => {
        if (project !== null) {
          this.project = project;
          this.selectedPdfImageMaxSize = project.pdfExportImageMaxSizeKB ?? 500;
          this.isLoading = false;
        }
      });

    this.filesDbPath = this.dbpathservice.dbPathProject();

    this.sharedservice.checklists
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((checklists: Checklist[]) => {
        if (checklists !== null) {
          this.checklists = checklists;
          this.setChartData();
        }
      });
    this.sharedservice.receiptforms
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((receiptforms: Checklist[]) => {
        if (receiptforms !== null) {
          this.receiptforms = receiptforms;
          this.setChartData();
        }
      });
    this.sharedservice.objects
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((objects: AventiObject[]) => {
        if (objects !== null) {
          this.objects = objects;
          this.setChartData();
          const types = [];

          const result = objects.reduce((r, a) => {
            r[a.type.name] = r[a.type.name] || [];
            r[a.type.name].push(a);
            return r;
          }, Object.create(null));

          for (const [key, value] of Object.entries(result)) {
            types.push({
              name: key,
              children: value,
            });
          }
          types.forEach((type) => {
            type.children.forEach((object) => {
              object.url = '/p/' + this.projectid + '/o/' + object.id;
              object.checklists?.forEach((checklist) => {
                checklist.url = object.url + '/s/' + checklist.id;
              });
              object.receiptforms?.forEach((receiptform) => {
                receiptform.url = object.url + '/s/' + receiptform.id;
              });
            });
          });
          this.dataSource.data = types;
        }
      });

    this.sharedservice.user
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((u) => {
        if (u != null) {
          this.user = u;
        }
      });

    this.sharedservice.users
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((users: User[]) => {
        if (users !== null) {
          this.userUsers = [];
          this.adminUsers = [];
          this.customerUsers = [];
          users.forEach((user) => {
            user.projectaccess?.forEach((pa) => {
              if (pa.id === this.projectid) {
                if (user.role?.name === 'user') {
                  this.userUsers.push(user);
                }
                if (user.role?.name === 'admin') {
                  this.adminUsers.push(user);
                }
                if (user.role?.name === 'customer') {
                  this.customerUsers.push(user);
                }
              }
            });
          });
        }

      });

  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  // Mirrors object-list: an object with a checklist that has any non-'not_started' status is "started".
  private isObjectStarted(object: AventiObject): boolean {
    return !!object.checklists?.some(cl => cl.status && cl.status !== 'not_started');
  }

  // Mirrors the chart's "Ikke startet" bucket: no status level reached, has checklist(s), work not begun.
  private isObjectNotStarted(object: AventiObject): boolean {
    return !object.status?.id && object.checklists?.length > 0 && !this.isObjectStarted(object);
  }

  setChartData() {
    if (this.statuses && this.objects && this.checklists) {
      const missingChecklistName = 'Mangler sjekkliste';
      const notStartedName = 'Ikke startet';
      const startedName = 'Påbegynt';
      const statusCount: Record<string, { count: number, priority: number }> = {};
      // Objects with no status level reached, split by checklist presence and whether work has begun.
      const missingChecklistCount = this.objects.filter(
        object => !object.status?.id && (!object.checklists || object.checklists.length === 0)
      ).length;
      const notStartedCount = this.objects.filter(
        object => this.isObjectNotStarted(object)
      ).length;
      const startedCount = this.objects.filter(
        object => !object.status?.id && object.checklists?.length > 0 && this.isObjectStarted(object)
      ).length;
      statusCount[missingChecklistName] = {count: missingChecklistCount, priority: -3};
      statusCount[notStartedName] = {count: notStartedCount, priority: -2};
      statusCount[startedName] = {count: startedCount, priority: -1};
      for (const status of this.statuses) {
        const count = this.objects.filter(object => object.status?.id === status.id).length;
        statusCount[status.name] = {count, priority: status.priority};
      }
      const statusCounts = [];
      const statusNames = [];
      for (const [key, value] of Object.entries(statusCount)) {
        statusCounts.push(value.count);
        statusNames.push(key + ' - ' + value.count);
      }
      let updateObjectsChartData = false;
      for (let i = 0; i < this.objectsChartData?.datasets[0].data.length; i++) {
        if (this.objectsChartData?.datasets[0].data[i] !== statusCounts[i]) {
          updateObjectsChartData = true;
        }
      }
      if (!this.objectsChartData || updateObjectsChartData) {
        this.objectsChartData = {
          labels: statusNames,
          datasets: [
            {
              label: 'Objekt status',
              data: statusCounts,
              backgroundColor: [
                'rgba(39, 133, 39, 0.4)',   // Primary Green
                'rgba(76, 175, 80, 0.4)',   // Light Green
                'rgba(129, 199, 132, 0.4)', // Pale Green
                'rgba(46, 125, 50, 0.4)',   // Dark Green
                'rgba(139, 195, 74, 0.4)',  // Lime Green
                'rgba(0, 150, 136, 0.4)',   // Teal
                'rgba(38, 166, 154, 0.4)',  // Light Teal
                'rgba(100, 181, 246, 0.4)', // Soft Blue
                'rgba(144, 164, 174, 0.4)', // Blue Grey
                'rgba(174, 213, 129, 0.4)', // Light Lime
                'rgba(200, 230, 201, 0.4)',  // Mint
                'rgba(158, 158, 158, 0.4)', // Grey (neutral)
              ],
              borderColor: [
                'rgb(39, 133, 39)',          // Primary Green
                'rgb(76, 175, 80)',          // Light Green
                'rgb(129, 199, 132)',        // Pale Green
                'rgb(46, 125, 50)',          // Dark Green
                'rgb(139, 195, 74)',         // Lime Green
                'rgb(0, 150, 136)',          // Teal
                'rgb(38, 166, 154)',         // Light Teal
                'rgb(100, 181, 246)',        // Soft Blue
                'rgb(144, 164, 174)',        // Blue Grey
                'rgb(174, 213, 129)',        // Light Lime
                'rgb(200, 230, 201)',         // Mint
                'rgb(158, 158, 158)',        // Grey
              ],
              borderWidth: 0,
              borderRadius: 8
            }
          ]
        };
      }

      // 'creating' is a legacy status that is never written, so it is intentionally omitted.
      const checklistStatuses = ['not_started', 'started', 'finished', 'signed'];
      const statusCountChecklists: Record<string, { count: number }> = {};
      for (const status of checklistStatuses) {
        const count = this.checklists.filter(checklist => checklist.status === status).length;
        statusCountChecklists[status] = {count};
      }
      const statusCountsChecklists = [];
      const statusNamesChecklists = [];
      for (const [key, value] of Object.entries(statusCountChecklists)) {
        statusCountsChecklists.push(value.count);
        const humanKey = this.sharedservice.humanizeStatus(key)[0];
        statusNamesChecklists.push(humanKey + ' - ' + value.count);
      }
      let updateChecklistsChartData = false;
      for (let i = 0; i < this.checklistsChartData?.datasets[0].data.length; i++) {
        if (this.checklistsChartData?.datasets[0].data[i] !== statusCountsChecklists[i]) {
          updateChecklistsChartData = true;
        }
      }

      if (!this.checklistsChartData || updateChecklistsChartData) {
        this.checklistsChartData = {
          labels: statusNamesChecklists,
          datasets: [
            {
              label: 'Sjekkliste status',
              data: statusCountsChecklists,
              backgroundColor: [
                'rgba(158, 158, 158, 0.4)',  // Grey  - Ikke startet
                'rgba(33, 150, 243, 0.4)',   // Blue  - Påbegynt
                'rgba(255, 193, 7, 0.4)',    // Amber - Ferdig
                'rgba(76, 175, 80, 0.4)',    // Green - Signert
              ],
              borderColor: [
                'rgba(158, 158, 158)',  // Grey  - Ikke startet
                'rgba(33, 150, 243)',   // Blue  - Påbegynt
                'rgba(255, 193, 7)',    // Amber - Ferdig
                'rgba(76, 175, 80)',    // Green - Signert
              ],
              borderWidth: 0,
              borderRadius: 8
            }
          ]
        };
      }

      const documentStyle = getComputedStyle(document.documentElement);
      // const textColor = documentStyle.getPropertyValue('--text-color');
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
      if (!this.chartOptions) {
        this.chartOptions = {
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                color: textColorSecondary
              },
              grid: {
                color: surfaceBorder,
                drawBorder: false
              }
            },
            x: {
              ticks: {
                color: textColorSecondary
              },
              grid: {
                color: surfaceBorder,
                drawBorder: false
              }
            }
          }
        };
      }
    }
  }

  export2json() {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob([JSON.stringify(this.objects, null, 2)], {
        type: 'application/json',
      })
    );
    a.setAttribute('download', this.project.id + ' - export.json');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async customerExport2Excel() {
    this.isLoadingCustomerExport = true;
    const customerExport = [];
    const allStatuses = await firstValueFrom(this.adminservice.getObjectStatuses());
    const allChecklistitems = await firstValueFrom(this.adminservice.getAllChecklistitemsObs());
    for (const object of this.objects) {

      const {
        object: updatedObject,
        sortedAllStatuses
      } = this.checklistservice.groupChecklistItems(
        object,
        object.checklists[0],
        false,
        this.project.useStatusLevel,
        allStatuses,
        allChecklistitems
      );
      const result = {
        Objektnavn: updatedObject.name ?? '',
        Arbeidspakke: updatedObject.comment ?? '',
        Beskrivelse: updatedObject.desc ?? '',
        Type: updatedObject.type.name ?? '',
        Prosess: updatedObject.process ?? '',
        Pnummer: updatedObject.profilenumber ?? '',
        TFM: updatedObject.tfmTag ?? '',
        RDS: updatedObject.rdsTag ?? '',
        Tags: updatedObject.tags ? updatedObject.tags.map(tag => `${tag.key}: ${tag.value}`).join() : '',
        'Antall Sjekklister': updatedObject.checklists ? updatedObject.checklists.length : 0,
        Status: updatedObject.status?.name ?? ''
      };
      // Same "Ikke startet" computation as the chart (setChartData), placed right after Status.
      result['0. ikke startet'] = this.isObjectNotStarted(object) ? 'X' : '';
      // Number prefix (1., 2., ...) on status columns for easier sorting; 0. is "ikke startet".
      sortedAllStatuses.forEach((status, index) => {
        result[`${index + 1}. ${status.name}`] = status.priority <= object.status?.priority ? 'X' : '';
      });
      customerExport.push(result);
    }
    this.excelService.exportAsExcelFile(customerExport, `${this.project.id} ${this.project.name} - customer`);
    this.isLoadingCustomerExport = false;
  }

  async toggleStatusLevelSetting(event) {
    this.project.useStatusLevel = event.checked;
    this.project.statusLevelUpdateInProgress = event.checked ? STATUS_LEVEL_UPDATE_STATE.REQUESTED : STATUS_LEVEL_UPDATE_STATE.DISABLED;
    this.project.statusLevelUpdateInProgressCount = 0;
    await this.projectservice.updateProject(this.project);
  }

  async onPdfImageMaxSizeChange(value: number) {
    this.selectedPdfImageMaxSize = Number(value);
    this.project.pdfExportImageMaxSizeKB = this.selectedPdfImageMaxSize;
    await this.projectservice.updateProject(this.project);
  }

  async regenerateProjectPdfs() {
    if (this.isRegeneratingProjectPdfs) {
      return;
    }
    this.regenStartedFromGroupId = this.latestPdfRegenerationGroup?.id;
    this.isRegeneratingProjectPdfs = true;
    this.pdfRegenerationCompletedToastShown = false;
    this.monotonicPdfRegenerationProgress = 0;
    this.monotonicProgressGroupId = undefined;
    this.pdfRegenerationState = {
      status: JOB_STATUS.QUEUED,
      progress: 0,
      totalCount: 0,
    };
    try {
      const response = await this.projectservice.regenerateProjectPdfs(
        this.projectid,
        Number(this.selectedPdfImageMaxSize),
        this.user
      );
      if (response.totalCount === 0) {
        this.isRegeneratingProjectPdfs = false;
        this.snackbarservice.openSnackBar(
          this.user.language?.id === 'en'
            ? 'No existing checklist PDFs to regenerate.'
            : 'Ingen eksisterende sjekkliste-PDF-er å regenerere.',
          'OK'
        );
      }
    } catch (error: any) {
      this.isRegeneratingProjectPdfs = false;
      this.snackbarservice.openSnackBar(
        error?.message || (
          this.user.language?.id === 'en'
            ? 'Could not start PDF regeneration.'
            : 'Kunne ikke starte regenerering av PDF-er.'
        ),
        'OK'
      );
    }
  }

  openPdfRegenerationJobsDialog() {
    this.dialog.open(PdfRegenerationJobsComponent, {
      width: this.mobileQuery.matches ? '96vw' : '920px',
      maxWidth: '96vw',
      data: {
        projectId: this.projectid,
        user: this.user,
        initialRtJobStates: this.rtPdfRegenerationSnapshot,
        initialState: this.pdfRegenerationState,
      },
    });
  }

  private async recomputePdfRegenerationState() {
    const group = this.latestPdfRegenerationGroup;
    // While a freshly clicked regen hasn't produced its own group yet, ignore the previous group's
    // cleaned-up state so the UI doesn't snap back to "done".
    const isStaleCompletedGroup = !!group && group.id === this.regenStartedFromGroupId;
    if (group?.payloadsCleanedAt && !isStaleCompletedGroup) {
      const finalStatus = group.state?.status || JOB_STATUS.COMPLETED;
      if (this.isRegeneratingProjectPdfs && !this.pdfRegenerationCompletedToastShown) {
        this.pdfRegenerationCompletedToastShown = true;
        const failed = group.failedCount || 0;
        const en = this.user?.language?.id === 'en';
        const msg = finalStatus === JOB_STATUS.FAILED || failed > 0
          ? (en ? `PDF regeneration finished with ${failed} failure(s).` : `PDF-regenerering ferdig med ${failed} feil.`)
          : (en ? 'PDF regeneration completed.' : 'Regenerering av PDF-er er ferdig.');
        this.snackbarservice.openSnackBar(msg, 'OK');
      }
      this.isRegeneratingProjectPdfs = false;
      this.monotonicProgressGroupId = group.id;
      this.monotonicPdfRegenerationProgress = 100;
      this.pdfRegenerationState = {
        status: finalStatus,
        progress: 100,
        totalCount: group.totalCount || 0,
      };
      return;
    }
    await this.setPdfRegenerationState(this.rtPdfRegenerationSnapshot);
  }

  private applyMonotonicProgress(progress: number, groupId: string | undefined, isTerminal: boolean): number {
    if (groupId && groupId !== this.monotonicProgressGroupId) {
      this.monotonicProgressGroupId = groupId;
      this.monotonicPdfRegenerationProgress = 0;
    }
    if (isTerminal) {
      this.monotonicPdfRegenerationProgress = Math.max(this.monotonicPdfRegenerationProgress, progress);
      return this.monotonicPdfRegenerationProgress;
    }
    const next = Math.max(this.monotonicPdfRegenerationProgress, progress);
    this.monotonicPdfRegenerationProgress = next;
    return next;
  }

  pdfRegenerationActivityLabel(): string {
    const en = this.user?.language?.id === 'en';
    const state = this.pdfRegenerationState;
    if (!state) {
      return '';
    }
    const queuedOrStarting = state.status === JOB_STATUS.QUEUED
      || (state.progress === 0 && state.status !== JOB_STATUS.COMPLETED && state.status !== JOB_STATUS.FAILED);
    if (queuedOrStarting) {
      return en ? 'Starting...' : 'Starter...';
    }
    const prefix = en ? 'Regenerating checklist PDFs... ' : 'Regenererer sjekkliste-PDF-er... ';
    return `${prefix}${state.progress}%`;
  }

  pdfRegenerationCompletedAtLabel(): string {
    const value = this.latestPdfRegenerationGroup?.createdAt as any;
    if (!value) {
      return '';
    }
    const date = typeof value?.toDate === 'function' ? value.toDate() : value;
    return date instanceof Date ? date.toLocaleString() : '';
  }

  private async setPdfRegenerationState(rtJob: any[]) {
    if (rtJob.length === 0) {
      if (this.isRegeneratingProjectPdfs) {
        this.pdfRegenerationState = this.pdfRegenerationState || {
          status: JOB_STATUS.QUEUED,
          progress: 0,
          totalCount: 0,
        };
      } else {
        this.pdfRegenerationState = {
          status: JOB_STATUS.COMPLETED,
          progress: 0,
          totalCount: 0,
        };
      }
      return;
    }

    const jobGroup = rtJob.find(rtJobState => (rtJobState as RtJobGroup).totalCount !== undefined) as RtJobGroup;
    const rtJobStates = rtJob.filter(rtJobState => (rtJobState as RtJobGroup).totalCount === undefined) as RtJobState[];
    const childProgressDivisor = jobGroup?.jobCount || rtJobStates.length;
    const childProgress = childProgressDivisor > 0
      ? Math.round(rtJobStates.map(rtJobState => rtJobState.progress || 0)
        .reduce((acc, progressValue) => acc + progressValue, 0) / childProgressDivisor)
      : 0;
    const progress = jobGroup?.progress !== undefined ? jobGroup.progress : childProgress;
    const childStatus = rtJobStates.some(state => state.status === JOB_STATUS.FAILED) ? JOB_STATUS.FAILED
      : (rtJobStates.length > 0 && rtJobStates.every(state => state.status === JOB_STATUS.COMPLETED) && progress >= 100 ? JOB_STATUS.COMPLETED
        : (rtJobStates.some(state => state.status === JOB_STATUS.SAVING_RTDB) ? JOB_STATUS.SAVING_RTDB
          : (rtJobStates.some(state => state.status === JOB_STATUS.SAVING_FIRESTORE) ? JOB_STATUS.SAVING_FIRESTORE
            : (rtJobStates.some(state => state.status === JOB_STATUS.IN_PROGRESS || state.status === JOB_STATUS.COMPLETED) ? JOB_STATUS.IN_PROGRESS : JOB_STATUS.QUEUED))));
    const status = jobGroup?.status === JOB_STATUS.COMPLETED && progress < 100
      ? JOB_STATUS.IN_PROGRESS
      : (jobGroup?.status || childStatus);

    const wasRunning = this.isRegeneratingProjectPdfs;
    const nowCompleted = status === JOB_STATUS.COMPLETED && progress >= 100;
    const nowFailed = status === JOB_STATUS.FAILED && progress >= 100;

    if (wasRunning && nowCompleted && !this.pdfRegenerationCompletedToastShown) {
      this.pdfRegenerationCompletedToastShown = true;
      this.snackbarservice.openSnackBar(
        this.user.language?.id === 'en'
          ? 'PDF regeneration completed.'
          : 'Regenerering av PDF-er er ferdig.',
        'OK'
      );
    }

    this.isRegeneratingProjectPdfs = !nowCompleted && !nowFailed;
    const groupKey = (jobGroup as any)?.id as string | undefined;
    const displayedProgress = this.applyMonotonicProgress(progress, groupKey, nowCompleted || nowFailed);
    this.pdfRegenerationState = {
      status,
      progress: displayedProgress,
      totalCount: jobGroup?.totalCount || 0,
    };

    // Live Firestore subscription can lag after long-running jobs. When we observe completion via
    // RTDB but the latest group doc hasn't propagated payloadsCleanedAt yet, force a one-shot read.
    if (wasRunning && (nowCompleted || nowFailed) && !this.latestPdfRegenerationGroup?.payloadsCleanedAt) {
      await this.refreshLatestPdfRegenerationGroup();
    }
  }

  private async refreshLatestPdfRegenerationGroup(): Promise<void> {
    try {
      const latest = await this.jobservice.getLatestPdfRegenerationJobGroupOnce(this.projectid);
      if (!latest) {
        return;
      }
      // Only overwrite if the fetched group is the same or newer, and gives us more info.
      const currentId = this.latestPdfRegenerationGroup?.id;
      if (!currentId || latest.id === currentId || (latest.payloadsCleanedAt && !this.latestPdfRegenerationGroup?.payloadsCleanedAt)) {
        if (this.regenStartedFromGroupId && latest.id !== this.regenStartedFromGroupId) {
          this.regenStartedFromGroupId = undefined;
        }
        this.latestPdfRegenerationGroup = latest;
        await this.recomputePdfRegenerationState();
      }
    } catch (e) {
      console.warn('Failed to refresh latest PDF regeneration group', e);
    }
  }

}
